import React, { useState, useCallback } from 'react';
import { Activity, AlertTriangle } from 'lucide-react';
import useAutoRefresh from '../../lib/useAutoRefresh';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Peak-Hour Health widget — compact, top-of-Ops-Dashboard.
 *
 * Renders p50/p95/p99 API latency and 5xx count for the last 15 min +
 * last 1 h. Data comes from ``GET /api/admin/health-metrics`` which
 * reads the in-memory rolling buffer populated by the FastAPI middleware.
 *
 * Cadence: 15 s (jittered by ``useAutoRefresh``) — fast enough to catch
 * capacity slippage during the morning burst, cheap enough to not itself
 * add load.
 */
export default function HealthMetricsWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const authHeader = () => {
    const tok = localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  const fetchNow = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/health-metrics`, { headers: { ...authHeader() } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setError('');
    } catch (e) {
      setError(e.message || 'Failed');
    }
  }, []);

  React.useEffect(() => { fetchNow(); }, [fetchNow]);
  useAutoRefresh(fetchNow, 15_000);

  const w15 = data?.last_15m;
  const w1h = data?.last_1h;

  // Heuristic health status:
  //   green  — p95 < 500 ms AND no 5xx in last 15 m
  //   amber  — p95 in [500, 1500] ms OR small 5xx count
  //   red    — p95 > 1500 ms OR any sustained 5xx burst (>10 in 15 m)
  const level = (() => {
    if (!w15 || w15.count === 0) return 'idle';
    if (w15.p95_ms > 1500 || w15.err_5xx > 10) return 'red';
    if (w15.p95_ms > 500 || w15.err_5xx > 0) return 'amber';
    return 'green';
  })();

  const statusColor = {
    idle:  'bg-gray-100 text-gray-500 border-gray-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red:   'bg-red-50 text-red-700 border-red-200',
  }[level];
  const dotColor = {
    idle:  'bg-gray-300',
    green: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red:   'bg-red-500 animate-pulse',
  }[level];
  const label = {
    idle:  'Idle — no traffic yet',
    green: 'Healthy',
    amber: 'Elevated',
    red:   'Saturating — investigate',
  }[level];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="health-metrics-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-gray-500" />
          <span className="font-semibold text-gray-800 text-sm">Backend Health</span>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${statusColor}`} data-testid="health-status-pill">
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
          {label}
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 flex items-center gap-1 mb-2">
          <AlertTriangle size={12} />
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <MetricColumn title="Last 15 minutes" w={w15} testid="col-15m" />
        <MetricColumn title="Last 1 hour" w={w1h} testid="col-1h" />
      </div>

      <div className="text-[10px] text-gray-400 mt-3 leading-tight">
        Per-pod stats. Data resets on redeploy. Warning if p95 &gt; 500 ms or 5xx appears in last 15 min.
      </div>
    </div>
  );
}

function MetricColumn({ title, w, testid }) {
  return (
    <div className="border border-gray-100 rounded-lg p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Cell label="Requests" value={fmt(w?.count)} />
        <Cell label="5xx" value={fmt(w?.err_5xx)} accent={w?.err_5xx > 0 ? 'text-red-600' : ''} />
        <Cell label="p50" value={ms(w?.p50_ms)} />
        <Cell label="p95" value={ms(w?.p95_ms)} accent={w?.p95_ms > 500 ? 'text-amber-600' : ''} />
        <Cell label="p99" value={ms(w?.p99_ms)} accent={w?.p99_ms > 1500 ? 'text-red-600' : ''} />
        <Cell label="max" value={ms(w?.max_ms)} />
      </div>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] text-gray-400">{label}</div>
      <div className={`font-semibold ${accent || 'text-gray-900'}`}>{value}</div>
    </div>
  );
}

function fmt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-IN');
}
function ms(n) {
  if (n == null) return '—';
  if (n < 1) return '<1 ms';
  if (n < 1000) return `${Math.round(n)} ms`;
  return `${(n / 1000).toFixed(2)} s`;
}
